import { useCallback, useMemo, useState } from 'react';
import { values as _values, size } from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { Result, ResultStatus } from '@/types/index';

type ApiCallbackResult<TResultData> = Promise<Result<TResultData> | void>;
type ApiCallback<TParams, TResultData> = (
  params: TParams
) => ApiCallbackResult<TResultData>;

type RequestApi<TParams, TResultData> = (
  params?: TParams
) => ApiCallbackResult<TResultData>;

type ApiErrorMessageKind1 = { code: string; description: string };

type UseApiHookResult<TParams, TResultData> = {
  isLoading: boolean;
  isSuccess: boolean | undefined;
  errors: string[] | undefined;
  request: RequestApi<TParams, TResultData>;
  result: Result<TResultData> | undefined;
};

const useApi = <TParams, TResultData>(
  fetchFromApi: ApiCallback<TParams, TResultData>
): UseApiHookResult<TParams, TResultData> => {
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState<boolean | undefined>();
  const [errors, setErrors] = useState<string[]>();
  const [result, setResult] = useState<Result<TResultData>>();

  const request: RequestApi<TParams, TResultData> = useCallback(
    async (values) => {
      setLoading(true);
      setSuccess(undefined);
      setErrors(undefined);

      try {
        const result: Result<TResultData> | void = await fetchFromApi(values);
        if (result?.status === ResultStatus.Error) {
          setSuccess(false);
          let errors: string[] = [];
          if (Array.isArray(result.data)) {
            errors = (result.data as ApiErrorMessageKind1[]).map(
              (error) => error.description
            );
          } else if (result.errors || size(result.data ?? {}) > 0) {
            errors = [..._values(result.errors || result.data)]?.flat();
          }
          if (!errors || !errors.length) {
            errors = [i18n._(t`An error occurred`)];
          }
          setErrors([...errors]);
        } else {
          setResult(result ?? { status: ResultStatus.Success });
          setSuccess(true);
        }
        return result;
      } catch (e) {
        setSuccess(false);
        const message =
          typeof e === 'string' ? e : e instanceof Error ? e.message : 'Unknown error';
        setErrors([message]);
      } finally {
        setLoading(false);
      }
      setLoading(false);
    },
    [fetchFromApi]
  );

  return useMemo(
    () => ({
      isLoading,
      isSuccess,
      errors,
      request,
      result,
    }),
    [request, result, errors, isSuccess, isLoading]
  );
};

export default useApi;
